import { X } from 'lucide-react'
import { useId, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useLocation } from 'react-router'
import { BackLayer, useBackHandler } from '../lib/useBack'
import { useScrollLock } from '../lib/useScrollLock'
import { dismissKeyboard } from '../lib/keyboard'
import { useKeyboardBoundary } from '../lib/useKeyboardBoundary'
import { Button } from './Button'

interface DialogProps {
  title: string
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
  /** Шире обычного: список с галочками. */
  wide?: boolean
  /** Большой редактор: весь доступный экран телефона, широкое окно на компьютере. */
  editor?: boolean
  /** Стабильный id прокрутки; задайте явно для нескольких окон с одинаковым заголовком. */
  scrollKey?: string
}

/**
 * На компьютере окно по центру, на телефоне — лист снизу. Закрывают Esc, «Назад»
 * Telegram (верхний слой стека, `useBackHandler`) и клик по фону. Лист с полем
 * поднимается над клавиатурой (`--kb`, lib/viewport.ts) и не заходит под
 * домашнюю полоску iPhone, даже без кнопок внизу.
 */
export function Dialog({
  title,
  onClose,
  children,
  footer,
  wide = false,
  editor = false,
  scrollKey,
}: DialogProps) {
  const titleId = useId()
  const location = useLocation()
  const keyboardBoundary = useKeyboardBoundary()
  const close = () => { dismissKeyboard(); onClose() }
  const layer = useBackHandler(close)
  useScrollLock()

  // В body: окно, открытое из перетаскиваемой карточки (у неё бывает transform), не съезжает.
  return createPortal(
    <BackLayer.Provider value={layer}>
      <div
        ref={keyboardBoundary}
        data-view-entry={location.key}
        className={`fixed inset-x-0 z-50 flex items-end justify-center desk:inset-0 desk:items-center desk:p-6 ${editor ? 'top-[var(--visual-top,0px)] h-[var(--visual-height,100dvh)] desk:h-auto' : 'top-0 bottom-[var(--kb)]'}`}
      >
        <div className="absolute inset-0 bg-black/45" onClick={close} aria-hidden />
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          className={`relative flex w-full flex-col bg-card shadow-xl ring-1 ring-line desk:max-h-[88dvh] desk:rounded-2xl ${
            editor
              ? 'h-full max-h-full pt-[var(--safe-top)] desk:h-[min(88dvh,960px)] desk:max-w-5xl desk:pt-0'
              : `max-h-[min(88dvh,calc(100%-var(--safe-top)-0.5rem))] rounded-t-2xl before:mx-auto before:mt-2 before:h-1 before:w-7 before:shrink-0 before:rounded-full before:bg-line desk:before:hidden ${wide ? 'desk:max-w-xl' : 'desk:max-w-md'}`
          }`}
        >
          <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-2.5">
            <h2 id={titleId} className="min-w-0 truncate text-base font-semibold">
              {title}
            </h2>
            <Button
              size="icon"
              variant="ghost"
              aria-label="Закрыть"
              onClick={close}
              className="-mr-2"
            >
              <X size={18} />
            </Button>
          </div>
          <div
            data-view-scroll={scrollKey ?? `dialog:${title}`}
            className={`min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pt-3 ${
              footer ? 'pb-3' : 'pb-[max(0.75rem,calc(var(--safe-bottom)-var(--kb)))] desk:pb-3'
            }`}
          >
            {children}
          </div>
          {footer && (
            <div
              className={`flex justify-end gap-2 border-t border-line px-4 pt-3 pb-[max(0.75rem,calc(var(--safe-bottom)-var(--kb)))] desk:pb-3 ${editor ? 'flex-nowrap' : 'flex-wrap'}`}
            >
              {footer}
            </div>
          )}
        </div>
      </div>
    </BackLayer.Provider>,
    document.body,
  )
}
